import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { blogPosts } from '../../mocks/blogPosts';

const categories = ['Tümü', 'Malzeme Rehberi', 'Teknoloji', 'Yasal Rehber', 'Tasarım', 'Dijital Çözümler', 'Sürdürülebilirlik', 'Ürün Rehberi', 'Lokasyon Rehberi'];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('Tümü');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.bioetiket.com';

    // Title
    document.title = 'Etiket Baskı Blog | Uzman Rehberler ve Sektör İpuçları | Bioetiket';

    // Description
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) { desc = document.createElement('meta'); desc.setAttribute('name', 'description'); document.head.appendChild(desc); }
    desc.setAttribute('content', 'Bioetiket Blog — Etiket baskı malzeme seçimi, dijital baskı teknolojileri, gıda etiketi yasal zorunlulukları ve sürdürülebilir etiket çözümleri hakkında uzman rehberler. İstanbul Ümraniye etiket üretimi.');

    // Keywords
    let kw = document.querySelector('meta[name="keywords"]');
    if (!kw) { kw = document.createElement('meta'); kw.setAttribute('name', 'keywords'); document.head.appendChild(kw); }
    kw.setAttribute('content', 'etiket baskı blog, etiket malzeme rehberi, gıda etiketi zorunlulukları, dijital baskı teknolojisi');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${siteUrl}/blog`);

    // OG tags
    const ogTags: Record<string, string> = {
      'og:title': 'Etiket Baskı Blog | Uzman Rehberler | Bioetiket',
      'og:description': 'Etiket baskı, malzeme seçimi, tasarım ve sektör haberleri hakkında uzman içerikler. Bioetiket Blog.',
      'og:url': `${siteUrl}/blog`,
      'og:type': 'website',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', property); document.head.appendChild(tag); }
      tag.setAttribute('content', content);
    });

    // Schema.org: Blog
    const schemaId = 'schema-blog-list';
    let existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Bioetiket Blog',
      url: `${siteUrl}/blog`,
      description: 'Etiket baskı, malzeme seçimi ve sektör rehberleri',
      publisher: {
        '@type': 'Organization',
        name: 'Bioetiket',
        url: siteUrl,
      },
    });
    document.head.appendChild(script);

    // Schema.org: ItemList (blog posts)
    const itemListId = 'schema-blog-itemlist';
    let existingIL = document.getElementById(itemListId);
    if (existingIL) existingIL.remove();
    const ilScript = document.createElement('script');
    ilScript.id = itemListId;
    ilScript.type = 'application/ld+json';
    ilScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Bioetiket Blog Yazıları',
      url: `${siteUrl}/blog`,
      itemListElement: blogPosts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${siteUrl}/blog/${post.slug}`,
        name: post.title,
      })),
    });
    document.head.appendChild(ilScript);

    // Schema.org: BreadcrumbList
    const bcId = 'schema-blog-breadcrumb';
    let existingBc = document.getElementById(bcId);
    if (existingBc) existingBc.remove();
    const bcScript = document.createElement('script');
    bcScript.id = bcId;
    bcScript.type = 'application/ld+json';
    bcScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
      ],
    });
    document.head.appendChild(bcScript);

    return () => {
      document.title = 'Ümraniye İstanbul Etiket Baskı | Profesyonel Etiket Basım | Bioetiket';
      const s = document.getElementById(schemaId);
      if (s) s.remove();
      const il = document.getElementById(itemListId);
      if (il) il.remove();
      const bc = document.getElementById(bcId);
      if (bc) bc.remove();
    };
  }, []);

  const filtered = blogPosts.filter((post) => {
    const matchCat = activeCategory === 'Tümü' || post.category === activeCategory;
    const matchSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-xs font-semibold tracking-widest text-[#FF6B35] uppercase">
            Uzman İçerikler
          </span>
          <h1 className="text-5xl font-bold text-[#1A2332] mt-4 mb-4 leading-tight">
            Etiket Baskı <span className="italic text-[#FF6B35]">Rehberi & Blog</span>
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
            Etiket baskı, malzeme seçimi, tasarım ve sektör haberleri hakkında uzman içeriklerimizle doğru kararlar verin.
          </p>

          {/* Arama */}
          <div className="max-w-lg mx-auto relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder="Makale ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-5 py-4 text-sm bg-white border border-gray-200 rounded-full shadow-sm focus:outline-none focus:border-[#FF6B35] transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Kategoriler */}
      <section className="sticky top-20 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-[#FF6B35] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-[#FF6B35]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Makaleler */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
                <i className="ri-article-line text-5xl"></i>
              </div>
              <p className="text-gray-500 text-base">Arama kriterlerinize uygun makale bulunamadı.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer flex flex-col"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                      width="800"
                      height="500"
                    />
                    <span className="absolute top-4 left-4 bg-[#FF6B35] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {new Date(post.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {post.readTime} okuma
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-[#1A2332] mb-3 group-hover:text-[#FF6B35] transition-colors leading-snug">
                      {post.title}
                    </h2>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-1 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs px-3 py-1 bg-orange-50 text-[#FF6B35] rounded-full">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#FF6B35]">
                      Devamını Oku
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A2332] mx-6 rounded-3xl mb-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Etiket İhtiyacınız mı Var?
          </h3>
          <p className="text-gray-400 text-base mb-8">
            Uzman ekibimiz size özel çözüm sunsun. Ücretsiz numune ve teklif için hemen iletişime geçin.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/#contact"
              className="inline-flex items-center px-8 py-4 text-sm font-semibold text-white bg-[#FF6B35] rounded-full hover:bg-[#E55A2B] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Ücretsiz Teklif Al
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a
              href="https://wa.me/905326018294?text=Merhaba%2C%20etiket%20bask%C4%B1%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center px-8 py-4 text-sm font-semibold text-white border-2 border-white/30 rounded-full hover:border-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-2 text-green-400"></i>
              WhatsApp'tan Yaz
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}